/**
 * What MO OS changed in his vault, and putting it back.
 *
 * WHY IT IS ON THE DECK. In one evening this system merged 296 topic notes, folded 23 people, dated
 * 1,301 threads and repointed twenty links. He watched all of that through an assistant's messages.
 * A system that rewrites thousands of your notes unattended, with no screen saying what it did and no
 * way to reverse one, is not a system you supervise.
 *
 * The panel is deliberately plain. Each row is one action, what it touched, when, and a control to
 * put it back. No charts, no colour beyond the refusal, because this is the screen he will read when
 * something looks wrong and he wants a fact.
 */

import { useCallback, useEffect, useState, type JSX } from 'react';
import { Icon } from './Icon.js';
import { fetchChanges, undoChange, type VaultChange } from './api.js';

/** `2026-08-07T01:52:00Z` to `01:52 today`, which is what he actually wants to know. */
function when(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const d = new Date(t);
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const days = Math.floor((Date.now() - t) / 86_400_000);
  if (days <= 0) return `${time} today`;
  if (days === 1) return `${time} yesterday`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export function ChangesPanel(): JSX.Element | null {
  const [items, setItems] = useState<VaultChange[] | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [refused, setRefused] = useState<{ id: number; reason: string } | null>(null);

  const load = useCallback(async () => {
    setItems(await fetchChanges());
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Nothing recorded yet, or an older server. Rendering an empty panel would imply nothing has
  // happened, which on this vault would be false.
  if (items === null || items.length === 0) return null;

  const onUndo = async (id: number): Promise<void> => {
    setBusy(id);
    setRefused(null);
    const res = await undoChange(id);
    setBusy(null);
    if (!res.ok) setRefused({ id, reason: res.reason });
    await load();
  };

  return (
    <section className="deck-panel" data-hue="know">
      <header className="deck-panel-head">
        <Icon name="holdings" size={16} className="deck-panel-icon" />
        <h2 className="deck-panel-title">What MO OS changed</h2>
      </header>
      <div className="deck-panel-body">
        <p className="deck-panel-note">
          Everything automatic that rewrote a note. Undo puts the notes back exactly as they were.
        </p>
        <ul className="mo-changes">
          {items.map((c) => (
            <li key={c.id} className="mo-change" data-undone={c.undone}>
              <div className="mo-change-line">
                <span className="mo-change-action">{c.action}</span>
                <span className="mo-change-when">{when(c.at)}</span>
              </div>
              <p className="mo-change-summary">
                {c.summary === '' ? `${c.noteCount} notes` : c.summary}
                <span className="mo-change-count"> · {c.noteCount} notes</span>
              </p>
              {c.undone ? (
                <span className="mo-change-done">Undone</span>
              ) : (
                <button
                  type="button"
                  className="deck-more"
                  disabled={busy === c.id}
                  onClick={() => void onUndo(c.id)}
                >
                  {busy === c.id ? 'Putting it back.' : 'Undo'}
                </button>
              )}
              {/*
                The refusal is the most important text in this panel. It appears when newer work
                would be discarded, and it names what changed since, so it reads as a reason rather
                than as a failure.
              */}
              {refused?.id === c.id && <p className="mo-change-refused">{refused.reason}</p>}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
